<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />
    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        use-back
        title="Grupos de notificação"
        subtitle="Configure quem recebe alertas para quais sensores e tipos de alarme.">
        <template #actions>
        <div class="flex items-center gap-3">

          <router-link
            :to="{ name: 'notification-groups-new' }"
            class="border border-primary bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90">
            Novo grupo de notificação
          </router-link>
        </div>
        </template>
      </PageHeader>

      <div class="space-y-4">
        <div
          v-for="ng in groups"
          :key="ng.id"
          class=" border border-stroke bg-white p-5 shadow-sm transition hover:shadow-md">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-3">
                <h3 class="text-lg font-semibold text-slate-900">{{ ng.name }}</h3>
                <span
                  :class="[
                    ' px-2.5 py-0.5 text-xs font-medium',
                    ng.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600',
                  ]">
                  {{ ng.active ? 'Ativo' : 'Inativo' }}
                </span>
              </div>
              <p v-if="ng.description" class="mt-1 text-sm text-slate-600">{{ ng.description }}</p>
              <div class="mt-3 flex flex-wrap gap-2">
                <span class="inline-flex items-center gap-1  bg-slate-100 px-2 py-1 text-xs text-slate-700">
                  <span class="font-medium">{{ ng.user_count }}</span> usuário(s)
                </span>
                <span class="inline-flex items-center gap-1  bg-slate-100 px-2 py-1 text-xs text-slate-700">
                  <span class="font-medium">{{ ng.group_count }}</span> grupo(s) de sensores
                </span>
                <span class="inline-flex items-center gap-1  bg-slate-100 px-2 py-1 text-xs text-slate-700">
                  <span class="font-medium">{{ ng.alarm_type_count }}</span> tipo(s) de alarme
                </span>
              </div>
              <div
                v-if="ng.users?.length || ng.groups?.length || ng.alarm_type_configs?.length"
                class="mt-3 space-y-2 border-t border-stroke pt-3 text-sm">
                <div v-if="ng.users?.length">
                  <span class="font-medium text-slate-600">Usuários:</span>
                  <span class="text-slate-700">{{ ng.users.map((u) => u.name).join(', ') }}</span>
                </div>
                <div v-if="ng.groups?.length">
                  <span class="font-medium text-slate-600">Grupos de sensores:</span>
                  <span class="text-slate-700">{{ ng.groups.map((g) => g.name).join(', ') }}</span>
                </div>
                <div v-if="ng.alarm_type_configs?.length">
                  <span class="font-medium text-slate-600">Tipos de alarme:</span>
                  <span class="text-slate-700">
                    {{ ng.alarm_type_configs.filter((a) => a.active).map((a) => translateAlarmType(a.alarm_type)).join(', ') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <router-link
                :to="{ name: 'notification-groups-edit', params: { id: String(ng.id) } }"
                class=" border border-stroke px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Editar
              </router-link>
              <button
                type="button"
                class=" border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
                @click="confirmDelete(ng)">
                Excluir
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="!groups.length"
          class=" border-2 border-dashed border-stroke p-12 text-center">
          <p class="text-slate-500">Nenhum grupo de notificação configurado.</p>
          <router-link
            :to="{ name: 'notification-groups-new' }"
            class="mt-4 inline-block  border border-primary bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
            Criar primeiro grupo
          </router-link>
        </div>
      </div>
    </div>

    <ConfirmationModal
      :is-open="isDeleteModalOpen"
      cancel-text="Cancelar"
      confirmation-text="Excluir"
      @cancel="isDeleteModalOpen = false"
      @add="executeDelete">
      <template #content>
        <div class="space-y-2">
          <h3 class="text-lg font-semibold text-slate-900">Excluir grupo de notificação?</h3>
          <p class="text-sm text-slate-600">
            O grupo <strong>{{ groupToDelete?.name }}</strong> será removido permanentemente.
          </p>
        </div>
      </template>
    </ConfirmationModal>
  </DefaultLayout>
</template>

<script setup lang="ts">
import NotificationGroupResource from '@/api/resources/notificationGroup'
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import { translateAlarmType } from '@/interfaces/enums'
import type { INotificationGroup } from '@/interfaces/entities/notificationGroup'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { onMounted, ref } from 'vue'

const isLoading = ref(true)
const groups = ref<INotificationGroup[]>([])
const isDeleteModalOpen = ref(false)
const groupToDelete = ref<INotificationGroup | null>(null)

const fetchGroups = async () => {
  try {
    const res = await NotificationGroupResource.list()
    groups.value = res?.data ?? res ?? []
  } catch (e) {
    groups.value = []
  }
}

const confirmDelete = (ng: INotificationGroup) => {
  groupToDelete.value = ng
  isDeleteModalOpen.value = true
}

const executeDelete = async () => {
  if (!groupToDelete.value) return
  try {
    await NotificationGroupResource.destroy(groupToDelete.value.id)
    groups.value = groups.value.filter((g) => g.id !== groupToDelete.value!.id)
    isDeleteModalOpen.value = false
    groupToDelete.value = null
  } catch {
    // Erro ao excluir grupo
  }
}

onMounted(async () => {
  isLoading.value = true
  await fetchGroups()
  isLoading.value = false
})
</script>
